// js/system/uiUtils.js
//
// Shared UI utility functions for Oscilla
//

// =============================================================
// DRAGGABLE
// =============================================================

/**
 * Make an element draggable by a handle element.
 * Skips drag initiation when clicking on interactive form elements.
 * Handles elements initially positioned with `right`/`bottom` by
 * converting to `left`/`top` on first drag.
 *
 * @param {HTMLElement} el       - The element to move
 * @param {HTMLElement} handleEl - The drag handle (defaults to el itself)
 */
export function makeDraggable(el, handleEl = el) {
  let isDragging = false;
  let offsetX = 0, offsetY = 0;

  function startDrag(clientX, clientY, target) {
    // Don't initiate drag from interactive elements
    const tag = target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON") return false;

    isDragging = true;

    // Convert right/bottom positioning to left/top on first drag
    const style = el.style;
    if ((style.right && style.right !== "auto") && (!style.left || style.left === "auto")) {
      const rect = el.getBoundingClientRect();
      style.left = `${rect.left}px`;
      style.right = "auto";
    }
    if ((style.bottom && style.bottom !== "auto") && (!style.top || style.top === "auto")) {
      const rect = el.getBoundingClientRect();
      style.top = `${rect.top}px`;
      style.bottom = "auto";
    }

    offsetX = clientX - el.offsetLeft;
    offsetY = clientY - el.offsetTop;
    handleEl.style.cursor = "grabbing";
    return true;
  }

  function moveDrag(clientX, clientY) {
    if (!isDragging) return;
    el.style.left = `${clientX - offsetX}px`;
    el.style.top = `${clientY - offsetY}px`;
  }

  function endDrag() {
    if (isDragging) {
      isDragging = false;
      handleEl.style.cursor = "grab";
    }
    dragListeners?.abort();
    dragListeners = null;
  }

  // The window's move and end listeners are there only while a drag is on:
  // added per call for the element's life, every closed editor and
  // countdown panel kept its five, and each one ran on every mouse move.
  let dragListeners = null;
  function listenWhileDragging() {
    dragListeners?.abort();
    dragListeners = new AbortController();
    const opts = { signal: dragListeners.signal };
    window.addEventListener("mousemove", (e) => moveDrag(e.clientX, e.clientY), opts);
    window.addEventListener("mouseup", endDrag, opts);
    window.addEventListener("touchmove", (e) => {
      if (isDragging && e.touches.length === 1) {
        const touch = e.touches[0];
        moveDrag(touch.clientX, touch.clientY);
        e.preventDefault();
      }
    }, { ...opts, passive: false });
    window.addEventListener("touchend", endDrag, opts);
    window.addEventListener("touchcancel", endDrag, opts);
  }

  // Mouse events
  handleEl.addEventListener("mousedown", (e) => {
    if (startDrag(e.clientX, e.clientY, e.target)) {
      listenWhileDragging();
      e.preventDefault();
    }
  });

  // Touch events
  handleEl.addEventListener("touchstart", (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      if (startDrag(touch.clientX, touch.clientY, e.target)) {
        listenWhileDragging();
        e.preventDefault();
      }
    }
  }, { passive: false });
}
